import React, {useState, useEffect, useCallback} from "react";
import {Button, message, Modal} from "antd";
import {useNavigate, useLocation} from "react-router-dom";
import {Helmet} from "react-helmet";
import CartItem from "./CartItem";
import {language} from "../../language/Language";
import {useCart} from "../../Context/CartContext";
import Loader2 from "../../Components/Loader2";
import {useCreateOrderFromCartMutation, useGetGovernorateQuery, useGetOffersQuery, useGetOneProductQuery, useGetRewardPointsQuery, useGetWorkHoursQuery} from "../../RTK/Slices/Slices/apiSliceMeat";
import {
    getCart,
    getCartTotal,
    getCartItemsCount,
    clearCart,
    removeFromCart,
    validateOrderData,
    prepareOrderData,
    calculateDiscountedPrice,
    saveCart,
} from "../../utils/localStorage";

// ============================================================
// أيقونات طرق الدفع - SVG مرسومة بنفس روح الألوان الأصلية لكل خدمة
// (تصميم مبسّط خاص بنا، مش نسخ للوجو التجاري الأصلي)
// ============================================================
const InstapayIcon = () => (
    <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="34" height="34" rx="9" fill="#0A2A6B" />
        <path d="M9 22L15.5 10.5H19L21.5 15.5H25L18.5 27H15L12.5 22H9Z" fill="#00B4A6" />
        <path d="M9 22L15.5 10.5H19L12.5 22H9Z" fill="#F2B705" />
    </svg>
);

const CashIcon = () => (
    <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="34" height="34" rx="9" fill="#E8F7EE" />
        <rect x="6" y="12" width="18" height="12" rx="2" fill="#2E9E5B" />
        <circle cx="15" cy="18" r="3.2" fill="#E8F7EE" />
        <rect x="10" y="8" width="18" height="12" rx="2" fill="#4CBE7D" stroke="#2E9E5B" strokeWidth="0.6" />
        <circle cx="19" cy="14" r="3.2" fill="#E8F7EE" />
    </svg>
);

const WalletIcon = () => (
    <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="34" height="34" rx="9" fill="#FBEAEA" />
        <rect x="6" y="10" width="22" height="15" rx="3" fill="#ffffff" stroke="#D9534F" strokeWidth="1.4" />
        <rect x="6" y="10" width="22" height="5" rx="2.5" fill="#D9534F" />
        <circle cx="24" cy="19" r="2.2" fill="#D9534F" />
        <circle cx="11" cy="28" r="2" fill="#EE2737" />
        <circle cx="17" cy="28" r="2" fill="#00A651" />
        <circle cx="23" cy="28" r="2" fill="#F58220" />
    </svg>
);

const ValuIcon = () => (
    <svg width="34" height="34" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="34" height="34" rx="9" fill="#E6F7F6" />
        <path
            d="M9 12v6c0 2.8 2.2 5 5 5s5-2.2 5-5v-6"
            stroke="#00A99D"
            strokeWidth="2.4"
            strokeLinecap="round"
            fill="none"
        />
        <circle cx="24.5" cy="12.5" r="2.3" fill="#00A99D" />
    </svg>
);

// ============================================================
// مكون يجيب سعر منتج واحد من الـ API ويحدث السلة فوراً
// ============================================================
const CartItemSyncer = ({ productId, count, onSynced }) => {
    const { data, isSuccess } = useGetOneProductQuery(productId, {
        refetchOnMountOrArgChange: true,
    });

    useEffect(() => {
        if (isSuccess && data?.data?.[0]) {
            const latest = data.data[0];
            const newDiscount = parseFloat(latest.default_sales_discount || 0);
            const newOriginalPrice = parseFloat(latest.price);
            const newUnitPrice = parseFloat(calculateDiscountedPrice(latest.price, newDiscount));

            const cart = getCart();
            const idx = cart.findIndex(i => i.product_id === productId);
            if (idx === -1) return;

            const oldUnitPrice = parseFloat(cart[idx].unit_price);
            if (Math.abs(newUnitPrice - oldUnitPrice) > 0.01 ||
                Math.abs(newOriginalPrice - parseFloat(cart[idx].original_price || cart[idx].unit_price)) > 0.01) {
                cart[idx] = {
                    ...cart[idx],
                    unit_price: newUnitPrice.toFixed(2),
                    price: (newUnitPrice * cart[idx].count).toFixed(2),
                    original_price: newOriginalPrice.toFixed(2),
                    discount: newDiscount,
                    stock: latest.stock,
                };
                saveCart(cart);
                onSynced(); // أخبر الـ Cart إنه يعيد القراءة من localStorage
            }
        }
    }, [isSuccess, data, productId, onSynced]);

    return null;
};


const texts = {
    ar: {
        deliveryTo: "سعر التوصيل إلى",
        deliveryTime: "وقت التوصيل:",
        hours: "ساعة",
        EGP: "جنيه",
    },
    en: {
        deliveryTo: "Delivery price to",
        deliveryTime: "Delivery time:",
        hours: "Hour",
        EGP: "EGP ",
    },
};
const t = texts[language === "en" ? "en" : "ar"];

// تحويل الوقت من صيغة 24 ساعة (زي "21:00") لصيغة 12 ساعة بالعربي (زي "9:00 م")
const formatTime12h = (time24) => {
    if (!time24) return "";
    const [hStr, mStr = "00"] = time24.split(":");
    let hours = parseInt(hStr, 10);
    if (isNaN(hours)) return time24;
    const period = hours >= 12 ? "م" : "ص";
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${hours}:${mStr} ${period}`;
};

const CartList = () => {
    // State management
    const [cartItems, setCartItems] = useState([]);
    const [cartTotal, setCartTotal] = useState("0.00");
    const [cartItemsCount, setCartItemsCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [isClearModalOpen, setIsClearModalOpen] = useState(false);
    const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
    const [isOutOfStockModalOpen, setIsOutOfStockModalOpen] = useState(false);
    const [outOfStockItems, setOutOfStockItems] = useState([]);
    // ملحوظة: مفيش قيمة ديفولت لطريقة الدفع — لازم اليوزر يختار بنفسه
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
    const [isGovModalOpen, setIsGovModalOpen] = useState(false);
    const [govSearchTerm, setGovSearchTerm] = useState("");

    // localStorage
    const userData = localStorage.getItem("user");
    const jsonUserData = userData ? JSON.parse(userData) : null;

    // Hooks
    const {updateCartCount} = useCart();
    const [createOrder, {isLoading: isCreatingOrder}] = useCreateOrderFromCartMutation();
    const { data: workHoursData } = useGetWorkHoursQuery();
    const navigate = useNavigate();
    const location = useLocation();

    // قراءة بيانات المستخدم من localStorage
    const userGovernorate = jsonUserData?.governorate?.name ?? null;
    console.log("userGovernorate", userGovernorate);

    // جلب بيانات المحافظات
    const {
        data: governorateData,
        refetch: refetchGovernorates,
    } = useGetGovernorateQuery();

    // جلب العروض الفعّالة (خصم عند شراء كمية محددة من منتج)
    const {
        data: offersData,
    } = useGetOffersQuery();

    // جلب رصيد نقاط المكافآت الخاصة بالعميل الحالي + معاينة النقاط المكتسبة من إجمالي السلة الحالي
    const {
        data: rewardPointsData,
    } = useGetRewardPointsQuery(
        {contactId: jsonUserData?.contact_id, orderTotal: cartTotal},
        {skip: !jsonUserData?.contact_id}
    );

    const [pointsToRedeem, setPointsToRedeem] = React.useState(0);
    const rewardPointsEnabled = !!rewardPointsData?.enabled;
    const redeemablePoints = rewardPointsData?.max_redeemable_points || 0;
    const redeemAmountPerUnitPoint = rewardPointsData?.redeem_amount_per_unit_point || 0;
    const redeemableAmountFull = rewardPointsData?.max_redeemable_amount || 0;
    const rewardPointsName = rewardPointsData?.points_name || (language === "ar" ? "نقطة" : "point");
    const pointsToEarn = rewardPointsData?.points_to_earn || 0;

    //نضمن إن القيمة المختارة متتجاوزش أقصى نقاط متاحة (لو الرصيد اتغيّر مثلا)
    const safePointsToRedeem = Math.min(pointsToRedeem, redeemablePoints);

    // حساب تفاصيل كل عرض مستحق على محتويات السلة الحالية (لعرض "خصم عرض كذا" لكل منتج على حدة)
    const appliedOffers = React.useMemo(() => {
        if (!offersData?.data?.length || !cartItems?.length) return [];

        const result = [];
        offersData.data.forEach((offer) => {
            const items = offer.items || [];
            if (!items.length) return;

            let timesPossible = null;
            let normalBundleTotal = 0;
            const itemNames = [];

            for (const offerItem of items) {
                const cartItem = cartItems.find(
                    (i) => parseInt(i.product_id) === parseInt(offerItem.product_id)
                );
                const orderedQty = cartItem ? parseFloat(cartItem.count) || 0 : 0;
                const requiredQty = parseFloat(offerItem.required_qty) || 0;

                if (requiredQty <= 0 || orderedQty <= 0) {
                    timesPossible = 0;
                    break;
                }

                const timesForThisItem = Math.floor(orderedQty / requiredQty);
                if (timesPossible === null || timesForThisItem < timesPossible) {
                    timesPossible = timesForThisItem;
                }

                const unitPrice = cartItem ? parseFloat(cartItem.unit_price) || 0 : parseFloat(offerItem.unit_price) || 0;
                normalBundleTotal += unitPrice * requiredQty;
                itemNames.push(offerItem.product_name);
            }

            if (!timesPossible || timesPossible < 1) return;

            const offerPrice = parseFloat(offer.offer_price) || 0;
            const discountPerBundle = normalBundleTotal - offerPrice;
            if (discountPerBundle <= 0) return;

            result.push({
                productName: itemNames.join(" + "),
                discount: discountPerBundle * timesPossible,
                label: `خصم عرض ${itemNames.join(" + ")}`,
            });
        });

        return result;
    }, [offersData, cartItems]);

    // إجمالي خصم كل العروض مجمعين
    const offersDiscount = React.useMemo(
        () => appliedOffers.reduce((sum, o) => sum + o.discount, 0),
        [appliedOffers]
    );

    // قيمة خصم نقاط المكافآت حسب عدد النقاط اللي العميل اختار يستخدمها (كل أو جزء)
    const rewardDiscount = safePointsToRedeem * redeemAmountPerUnitPoint;



    console.log("governorateData", governorateData?.data);

    // استخراج سعر المحافظة
    // const deliveryPrice = governorateData?.data?.find((gov) => gov.name === userGovernorate)?.price;
    const deliveryPrice = governorateData?.data?.find((gov) => gov.name === userGovernorate)?.price || 0;

    console.log("deliveryPrice", deliveryPrice);

    // إنشاء العنوان الكامل من بيانات المستخدم بالترتيب الصحيح
    const getFullAddress = useCallback(() => {
        if (!jsonUserData) return "";

        const parts = [];

        // المحافظة / منطقة التوصيل
        if (jsonUserData.governorate?.name) {
            parts.push(jsonUserData.governorate.name);
        }

        // اسم الشارع (address_line_1 في الـ DB — حقل address في الـ user)
        if (jsonUserData.address) {
            parts.push(`شارع ${jsonUserData.address}`);
        }

        // رقم المبنى (مخزّن في حقل city)
        if (jsonUserData.city) {
            parts.push(`عمارة ${jsonUserData.city}`);
        }

        // رقم الدور (مخزّن في حقل street)
        if (jsonUserData.street) {
            parts.push(`دور ${jsonUserData.street}`);
        }

        // رقم الشقة (مخزّن في حقل building)
        if (jsonUserData.building) {
            parts.push(`شقة ${jsonUserData.building}`);
        }

        return parts.join(" - ");
    }, [jsonUserData]);

    // إعداد بيانات الشحن الحقيقية من بيانات المستخدم
    const getShippingInfo = useCallback(() => {
        if (!jsonUserData) {
            return {
                governarte: 1,
                shipping_address: "عنوان غير محدد",
                shipping_charges: 50,
                delivered_to: "مستخدم",
                shipping_details: "لا توجد معلومات إضافية",
            };
        }

        // بناء shipping_details بالتفاصيل الكاملة للموظفين
        const detailParts = [];
        if (jsonUserData.mobile)              detailParts.push(`الهاتف: ${jsonUserData.mobile}`);
        if (jsonUserData.governorate?.name)   detailParts.push(`المنطقة: ${jsonUserData.governorate.name}`);
        if (jsonUserData.address)             detailParts.push(`الشارع: ${jsonUserData.address}`);
        if (jsonUserData.city)                detailParts.push(`عمارة: ${jsonUserData.city}`);
        if (jsonUserData.street)              detailParts.push(`دور: ${jsonUserData.street}`);
        if (jsonUserData.building)            detailParts.push(`شقة: ${jsonUserData.building}`);

        return {
            governarte: jsonUserData.governorate?.id || 1,
            shipping_address: getFullAddress(),
            shipping_charges: parseFloat(jsonUserData.governorate?.price || "0"),
            delivered_to: jsonUserData.name || "مستخدم",
            shipping_details: detailParts.join(" | "),
            contact_id: jsonUserData.contact_id,
            user_mobile: jsonUserData.mobile,
        };
    }, [jsonUserData, getFullAddress]);

    // Payment methods configuration with correct API values
    const PAYMENT_METHODS = React.useMemo(
        () => [
            {
                key: "instapay",
                apiValue: "instapay", // القيمة التي سترسل للـ API
                label: language === "ar" ? "انستاباي" : "Instapay",
                icon: <InstapayIcon />,
                available: true,
                description: language === "ar" ? "الدفع بالبطاقة الائتمانية" : "Credit card payment",
            },
            {
                key: "cod",
                apiValue: "cod", // القيمة التي سترسل للـ API
                label: language === "ar" ? "الدفع نقدا كاش" : "Cash Payment",
                icon: <CashIcon />,
                available: true,
                description: language === "ar" ? "ادفع عند استلام الطلب" : "Pay upon receiving your order",
            },
            {
                key: "wallet",
                apiValue: "wallet", // القيمة التي سترسل للـ API
                label: language === "ar" ? "المحفظة الإلكترونية" : "Digital Wallet",
                icon: <WalletIcon />,
                available: true,
                description: language === "ar" ? "الدفع عبر المحفظة الإلكترونية" : "Pay via digital wallet",
            },
            {
                key: "valu",
                apiValue: "valu", // القيمة التي سترسل للـ API
                label: "Valu",
                icon: <ValuIcon />,
                available: true,
                description: language === "ar" ? "الدفع عبر valu" : "Pay via valu",
            },
        ],
        []
    );

    const LOADING_DELAY = 500;

    // Helper function to get payment method API value
    const getPaymentMethodApiValue = useCallback(
        (selectedMethod) => {
            const method = PAYMENT_METHODS.find((m) => m.key === selectedMethod);
            return method ? method.apiValue : "cod"; // fallback to cod if not found
        },
        [PAYMENT_METHODS]
    );

    // Memoized functions

    const updateCartData = useCallback(() => {
        const items = getCart();
        const total = getCartTotal();
        const count = getCartItemsCount();

        setCartItems(items);
        setCartTotal(total);
        setCartItemsCount(count);
    }, []);


    const handleClearCart = useCallback(() => {
        try {
            const success = clearCart();
            if (success) {
                updateCartData();
                updateCartCount();
                message.success(language === "ar" ? "تم تفريغ السلة بنجاح!" : "Cart cleared successfully!");
                setIsClearModalOpen(false);
            } else {
                throw new Error("Failed to clear cart");
            }
        } catch (error) {
            message.error(language === "ar" ? "فشل في تفريغ السلة" : "Failed to clear cart");
        }
    }, [updateCartData, updateCartCount]);

    const handleConfirmOrder = useCallback(async () => {
        if (cartItems.length === 0) {
            message.warning(language === "ar" ? "السلة فارغة!" : "Cart is empty!");
            return;
        }

        // التحقق من وجود بيانات المستخدم - توجيه لتسجيل الدخول مع حفظ المسار للعودة
        if (!jsonUserData || !jsonUserData.contact_id) {
            navigate("/Login", {state: {from: location.pathname}});
            return;
        }

        // التحقق من إن منطقة التوصيل المحفوظة للعميل لسه متاحة (مش مستبعدة من الإدارة).
        // بنجيب أحدث حالة فعلية من السيرفر هنا تحديدًا (مش من الكاش) عشان لو
        // الإدارة استبعدت المنطقة أو رجّعتها بعد ما الصفحة فُتحت، القرار يبقى
        // مبني على آخر حالة حقيقية وقت تأكيد الأوردر بالظبط.
        // بنطابق بالاسم (userGovernorate) مش بالـ id، لأن بيانات المستخدم
        // المحفوظة مش دايمًا فيها id - بس الاسم موجود ومضمون دايمًا
        // (وهو نفسه اللي بيتحسب بيه سعر الشحن فوق).
        let govList = governorateData?.data ?? [];
        try {
            const freshResult = await refetchGovernorates();
            govList = freshResult?.data?.data ?? govList;
        } catch (e) {
            // لو فشل التحديث لأي سبب، نكمل بالبيانات المتاحة عندنا بدل ما نوقف العميل تمامًا
        }
        const isAreaStillActive = govList.some(
            (gov) => gov.name === userGovernorate && gov.status === 1
        );
        if (!userGovernorate || !isAreaStillActive) {
            Modal.error({
                title: language === "ar" ? "عذراً" : "Sorry",
                content:
                    language === "ar"
                        ? "عذراً، التوصيل غير متاح لموقعكم الحالي."
                        : "Sorry, delivery is not available to your current location.",
                okText: language === "ar" ? "حسناً" : "OK",
                centered: true,
            });
            return;
        }

        // لازم اليوزر يختار طريقة دفع — مفيش ديفولت بيتفرض عليه
        if (!selectedPaymentMethod) {
            message.warning(
                language === "ar"
                    ? "من فضلك اختار طريقة الدفع أولاً"
                    : "Please select a payment method first"
            );
            return;
        }

        // Check if selected payment method is available
        const selectedMethod = PAYMENT_METHODS.find((method) => method.key === selectedPaymentMethod);
        if (!selectedMethod?.available) {
            message.error(
                language === "ar"
                    ? "طريقة الدفع المختارة غير متاحة حالياً"
                    : "The selected payment method is currently unavailable"
            );
            return;
        }

        // For cash on delivery, show confirmation modal
        if (selectedPaymentMethod === "cod") {
            setIsConfirmModalOpen(true);
            return;
        }

        // For other payment methods, also show confirmation modal
        // يمكنك هنا إضافة منطق مختلف لكل طريقة دفع إذا كنت تريد
        setIsConfirmModalOpen(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cartItems.length, selectedPaymentMethod, PAYMENT_METHODS, jsonUserData, governorateData]);

    const handleConfirmCODOrder = useCallback(async () => {
        try {
            const contactId = jsonUserData?.contact_id;
            const shippingInfo = getShippingInfo();

            console.log("Shipping Info:", shippingInfo); // للتأكد من البيانات

            // Validate order data first
            const validation = validateOrderData(contactId, shippingInfo);
            if (!validation.isValid) {
                message.error(
                    language === "ar"
                        ? `خطأ: ${validation.errors.join(", ")}`
                        : `Error: ${validation.errors.join(", ")}`
                );
                return;
            }

            // Get the correct API value for the selected payment method
            const paymentMethodValue = getPaymentMethodApiValue(selectedPaymentMethod);

            // Prepare order data with correct payment method
            const orderPrep = prepareOrderData(contactId, {
                ...shippingInfo,
                payment_method: paymentMethodValue,
                redeem_points: safePointsToRedeem,
            });

            if (!orderPrep.success) {
                message.error(language === "ar" ? `خطأ: ${orderPrep.error}` : `Error: ${orderPrep.error}`);
                return;
            }

            console.log("Order Data:", orderPrep.data);

            // Create order (first attempt) to check stock from API
            let apiResponse = await createOrder(orderPrep.data).unwrap();

            // السيرفر بيرجع 200 حتى لو الطلب فشل فعليًا (success: false)
            // لازم نتحقق من القيمة دي صراحةً قبل ما نعتبر الطلب نجح
            if (!apiResponse?.success) {
                throw new Error(apiResponse?.msg || (language === "ar" ? "فشل في إنشاء الطلب" : "Failed to create order"));
            }

            // Handle stock validation from API
            const productsStock = Array.isArray(apiResponse?.products_stock) ? apiResponse.products_stock : [];

            // If API indicates some products are out of stock, show modal
            if (productsStock.length > 0) {
                const unavailableItems = productsStock.filter(
                    (p) => p?.status === false || parseFloat(p?.stock ?? "0") <= 0
                );
                // eslint-disable-next-line no-unused-vars
                const availableItems = productsStock.filter(
                    (p) => p?.status === true && parseFloat(p?.stock ?? "0") > 0
                );

                if (unavailableItems.length > 0) {
                    // حفظ المنتجات غير المتاحة وعرض Modal للمستخدم
                    console.log("Found out of stock items from API:", unavailableItems);
                    setOutOfStockItems(unavailableItems);
                    setIsConfirmModalOpen(false); // إغلاق Modal التأكيد أولاً
                    setIsOutOfStockModalOpen(true);
                    console.log("Out of stock modal should be open now");
                    return; // إيقاف العملية هنا وانتظار قرار المستخدم
                }
            }

            // If we reach here, either all items were available or API didn't send stock details
            // Clear cart and show success message
            const clearSuccess = clearCart();
            if (clearSuccess) {
                updateCartData();
                updateCartCount();
                setIsConfirmModalOpen(false);
                message.success(language === "ar" ? "تم إنشاء الطلب بنجاح!" : "Order created successfully!");
            }
        } catch (error) {
            const errorMessage = error?.message || error?.data?.message || "خطأ غير معروف";
            message.error(
                language === "ar" ? `فشل في إنشاء الطلب: ${errorMessage}` : `Failed to create order: ${errorMessage}`
            );
        }
    }, [
        createOrder,
        updateCartData,
        updateCartCount,
        selectedPaymentMethod,
        jsonUserData,
        getShippingInfo,
        getPaymentMethodApiValue,
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ]);

    const handleContinueShopping = useCallback(() => {
        navigate("/products");
    }, [navigate]);

    const handleOpenClearModal = useCallback(() => {
        setIsClearModalOpen(true);
    }, []);

    const handleCloseClearModal = useCallback(() => {
        setIsClearModalOpen(false);
    }, []);

    const handleCloseConfirmModal = useCallback(() => {
        setIsConfirmModalOpen(false);
    }, []);

    const handleCloseOutOfStockModal = useCallback(() => {
        setIsOutOfStockModalOpen(false);
        setOutOfStockItems([]);
    }, []);

    // تغيير منطقة التوصيل من البوب‑أب
    const handlePickGovernorate = useCallback((gov) => {
        try {
            const user = JSON.parse(localStorage.getItem("user") || "{}");
            user.governorate = { id: gov.id, name: gov.name, price: gov.price, time_val: gov.time_val };
            localStorage.setItem("user", JSON.stringify(user));
            window.location.reload();
        } catch (e) { console.error(e); }
        setIsGovModalOpen(false);
        setGovSearchTerm("");
    }, []);

    const handleProceedWithAvailableItems = useCallback(async () => {
        // إغلاق Modal التحذيري
        setIsOutOfStockModalOpen(false);

        // حذف المنتجات غير المتاحة من السلة
        outOfStockItems.forEach((item) => {
            try {
                removeFromCart(item.product_id);
            } catch {}
        });

        // تحديث بيانات السلة
        updateCartData();
        updateCartCount();

        // المتابعة مع إنشاء الطلب
        await createOrderWithAvailableItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outOfStockItems, updateCartData, updateCartCount]);

    const createOrderWithAvailableItems = useCallback(async () => {
        try {
            const contactId = jsonUserData?.contact_id;
            const shippingInfo = getShippingInfo();

            // التحقق من وجود منتجات متاحة
            const currentCart = getCart();
            const inStockItems = currentCart.filter((item) => parseFloat(item?.stock ?? "0") > 0);

            if (inStockItems.length === 0) {
                message.error(
                    language === "ar"
                        ? "لا توجد منتجات متاحة في السلة لإتمام الطلب"
                        : "No available items in cart to place an order"
                );
                return;
            }

            // Validate order data
            const validation = validateOrderData(contactId, shippingInfo);
            if (!validation.isValid) {
                message.error(
                    language === "ar"
                        ? `خطأ: ${validation.errors.join(", ")}`
                        : `Error: ${validation.errors.join(", ")}`
                );
                return;
            }

            // Get the correct API value for the selected payment method
            const paymentMethodValue = getPaymentMethodApiValue(selectedPaymentMethod);

            // Prepare order data with correct payment method
            const orderPrep = prepareOrderData(contactId, {
                ...shippingInfo,
                payment_method: paymentMethodValue,
                redeem_points: safePointsToRedeem,
            });

            if (!orderPrep.success) {
                message.error(language === "ar" ? `خطأ: ${orderPrep.error}` : `Error: ${orderPrep.error}`);
                return;
            }

            // Create order
            const apiResponse = await createOrder(orderPrep.data).unwrap();

            // السيرفر بيرجع 200 حتى لو الطلب فشل فعليًا (success: false)
            // لازم نتحقق من القيمة دي صراحةً قبل ما نعتبر الطلب نجح
            if (!apiResponse?.success) {
                throw new Error(apiResponse?.msg || (language === "ar" ? "فشل في إنشاء الطلب" : "Failed to create order"));
            }

            // Handle stock validation from API
            const productsStock = Array.isArray(apiResponse?.products_stock) ? apiResponse.products_stock : [];

            if (productsStock.length > 0) {
                const unavailableItems = productsStock.filter(
                    (p) => p?.status === false || parseFloat(p?.stock ?? "0") <= 0
                );
                // eslint-disable-next-line no-unused-vars
                const availableItems = productsStock.filter(
                    (p) => p?.status === true && parseFloat(p?.stock ?? "0") > 0
                );

                if (availableItems.length === 0) {
                    const names = unavailableItems.map((p) => p?.name).filter(Boolean);
                    unavailableItems.forEach((prod) => {
                        try {
                            removeFromCart(prod.id);
                        } catch {}
                    });

                    updateCartData();
                    updateCartCount();
                    setIsConfirmModalOpen(false);

                    message.error(
                        language === "ar"
                            ? `عذراً، جميع المنتجات نفدت كميتها وتم حذفها من السلة: ${names.join(", ")}`
                            : `Sorry, all items are out of stock and were removed from the cart: ${names.join(", ")}`
                    );
                    return;
                }

                // Retry creating order with only available product IDs
                const allowedIds = new Set(availableItems.map((p) => parseInt(p.id)));
                const filteredProducts = orderPrep.data.products.filter((p) => allowedIds.has(parseInt(p.id)));

                if (filteredProducts.length !== orderPrep.data.products.length) {
                    const retryData = {
                        ...orderPrep.data,
                        products: filteredProducts,
                    };
                    const retryResponse = await createOrder(retryData).unwrap();

                    if (!retryResponse?.success) {
                        throw new Error(retryResponse?.msg || (language === "ar" ? "فشل في إنشاء الطلب" : "Failed to create order"));
                    }

                    // eslint-disable-next-line no-unused-vars
                    const removedNames = unavailableItems.map((p) => p?.name).filter(Boolean);

                    // Remove out-of-stock items from localStorage cart
                    unavailableItems.forEach((prod) => {
                        try {
                            removeFromCart(prod.id);
                        } catch {}
                    });

                    // Clear remaining ordered items from cart
                    clearCart();

                    updateCartData();
                    updateCartCount();
                    setIsConfirmModalOpen(false);
                    message.success(
                        language === "ar"
                            ? "تم إنشاء الطلب للمنتجات المتاحة وتم حذف غير المتاحة من السلة"
                            : "Ordered available items. Out-of-stock items were removed from cart."
                    );
                    return;
                }
            }

            // If we reach here, either all items were available or API didn't send stock details
            const clearSuccess = clearCart();
            if (clearSuccess) {
                updateCartData();
                updateCartCount();
                setIsConfirmModalOpen(false);
                message.success(language === "ar" ? "تم إنشاء الطلب بنجاح!" : "Order created successfully!");
            }
        } catch (error) {
            const errorMessage = error?.message || error?.data?.message || "خطأ غير معروف";
            message.error(
                language === "ar" ? `فشل في إنشاء الطلب: ${errorMessage}` : `Failed to create order: ${errorMessage}`
            );
        }
    }, [
        createOrder,
        updateCartData,
        updateCartCount,
        selectedPaymentMethod,
        jsonUserData,
        getShippingInfo,
        getPaymentMethodApiValue,
    ]);

    const handlePaymentMethodChange = useCallback((methodKey) => {
        setSelectedPaymentMethod(methodKey);
    }, []);

    // Get selected payment method label for display
    const getSelectedPaymentMethodLabel = useCallback(() => {
        const method = PAYMENT_METHODS.find((m) => m.key === selectedPaymentMethod);
        return method ? method.label : "";
    }, [selectedPaymentMethod, PAYMENT_METHODS]);

    // Effects
    useEffect(() => {
        const timer = setTimeout(() => {
            updateCartData();
            setIsLoading(false);
        }, LOADING_DELAY);

        return () => clearTimeout(timer);
    }, [updateCartData]);

    // Render loading state
    if (isLoading) {
        return (
            <div className="container mt-28 h-[70vh] grid place-items-center">
                <Loader2 />
            </div>
        );
    }

    // Render empty cart state
    if (cartItems.length === 0) {
        return (
            <div className="container mt-28 min-h-screen">
                <Helmet>
                    <title>Meat-House El-Fardos</title>
                    <meta
                        name="description"
                        content="راجع منتجاتك المختارة من اللحوم في سلة Meat House وأكمل عملية الشراء بسهولة وأمان."
                    />
                </Helmet>
                <div className="flex justify-between items-center mb-6">
                    <h2 className="text-xl sm:text-3xl font-extrabold text-gray-800">
                        {language === "ar" ? "سلة الشراء" : "Shopping Cart"}
                    </h2>
                    <div className="text-sm text-gray-600">(0 {language === "ar" ? "عنصر" : "items"})</div>
                </div>

                <div className="text-center py-20">
                    <div className="text-6xl text-gray-300 mb-4">🛒</div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">
                        {language === "ar" ? "السلة فارغة" : "Cart is Empty"}
                    </h3>
                    <p className="text-gray-500 mb-6">
                        {language === "ar"
                            ? "لم تقم بإضافة أي منتجات إلى السلة بعد"
                            : "You haven't added any products to your cart yet"}
                    </p>
                    <Button type="primary" size="large" onClick={handleContinueShopping}>
                        {language === "ar" ? "تسوق الآن" : "Shop Now"}
                    </Button>
                </div>
            </div>
        );
    }

    // Render cart with items
    return (
        <div className="container mt-28 min-h-screen">
            <Helmet>
                <title>Meat-House El-Fardos</title>
                <meta
                    name="description"
                    content="راجع منتجاتك المختارة من اللحوم في سلة Meat House وأكمل عملية الشراء بسهولة وأمان."
                />
            </Helmet>
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl sm:text-3xl font-extrabold text-gray-800">
                    {language === "ar" ? "سلة الشراء" : "Shopping Cart"}
                </h2>

                <div className="text-sm text-gray-600">
                    ({cartItemsCount} {language === "ar" ? "عنصر" : "items"})
                </div>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Products List */}
                <div className="lg:col-span-2">
                    <div className="bg-white rounded-lg shadow-md p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-lg font-semibold">{language === "ar" ? "المنتجات" : "Products"}</h3>
                            <Button danger size="small" onClick={handleOpenClearModal} disabled={isCreatingOrder}>
                                {language === "ar" ? "تفريغ السلة" : "Clear Cart"}
                            </Button>
                        </div>

                        {/* مزامنة أسعار كل منتج في السلة مع الـ API فوراً */}
                        {cartItems.map((item) => (
                            <CartItemSyncer
                                key={`sync-${item.product_id}`}
                                productId={item.product_id}
                                count={item.count}
                                onSynced={updateCartData}
                            />
                        ))}
                        <div className="space-y-4">
                            {cartItems.map((item) => (
                                <CartItem key={item.id} item={item} onUpdateCart={updateCartData} />
                            ))}
                        </div>

                        {/* مواعيد العمل والتوصيل — تظهر تحت المنتجات مباشرةً، وبتعرض المواعيد الفعلية الحقيقية */}
                        {workHoursData && (workHoursData.today || workHoursData.data?.note) && (
                            <div style={{
                                background: workHoursData.is_open_now ? "#fff8e1" : "#fdecea",
                                border: workHoursData.is_open_now ? "1px solid #ffe082" : "1px solid #f5c6cb",
                                borderRadius: 10,
                                padding: "12px 16px",
                                marginTop: 16,
                                display: "flex",
                                alignItems: "flex-start",
                                gap: 10,
                                direction: "rtl",
                            }}>
                                <span style={{ fontSize: 22, flexShrink: 0 }}>🕐</span>
                                <div>
                                    <p style={{ fontWeight: "bold", color: workHoursData.is_open_now ? "#795548" : "#b02a37", margin: 0, fontFamily: "Cairo, sans-serif", fontSize: 14 }}>
                                        {workHoursData.is_open_now ? "مواعيد العمل والتوصيل — المتجر مفتوح الآن" : "مواعيد العمل والتوصيل — المتجر مغلق حالياً"}
                                    </p>
                                    {workHoursData.today && (
                                        <p style={{ color: workHoursData.is_open_now ? "#5d4037" : "#842029", margin: "4px 0 0", fontSize: 13, fontFamily: "Cairo, sans-serif", lineHeight: 1.7 }}>
                                            مواعيد اليوم من <strong>{formatTime12h(workHoursData.today.open_time)}</strong> إلى <strong>{formatTime12h(workHoursData.today.close_time)}</strong>
                                        </p>
                                    )}
                                    {!workHoursData.is_open_now && workHoursData.message && (
                                        <p style={{ color: "#842029", margin: "4px 0 0", fontSize: 13, fontFamily: "Cairo, sans-serif", lineHeight: 1.7 }}>
                                            {workHoursData.message}
                                        </p>
                                    )}
                                    {workHoursData.data?.note && (
                                        <p style={{ color: "#5d4037", margin: "4px 0 0", fontSize: 13, fontFamily: "Cairo, sans-serif", lineHeight: 1.7 }}>
                                            {workHoursData.data.note}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                    </div>
                </div>

                {/* Order Summary */}
                <div className="lg:col-span-1">
                    <div className="bg-white rounded-lg shadow-md p-6 sticky top-6">
                        <h3 className="text-lg font-semibold mb-4">
                            {language === "ar" ? "ملخص الطلب" : "Order Summary"}
                        </h3>

                        <div className="space-y-3">
                            <div className="flex justify-between">
                                <span className="text-gray-600">{language === "ar" ? "عدد المنتجات:" : "Items:"}</span>
                                <span className="font-medium">{cartItemsCount}</span>
                            </div>

                            <div className="flex justify-between">
                                <span className="text-gray-600">
                                    {language === "ar" ? "المجموع الفرعي:" : "Subtotal:"}
                                </span>
                                <span className="font-medium text-mainColor">EGP {parseInt(cartTotal).toFixed(0)}</span>
                            </div>

                            {appliedOffers.map((offer, idx) => (
                                <div className="flex justify-between" key={idx}>
                                    <span className="text-gray-600">
                                        {language === "ar" ? offer.label + ":" : `${offer.productName} offer discount:`}
                                    </span>
                                    <span className="font-medium text-green-600">
                                        - EGP {offer.discount.toFixed(0)}
                                    </span>
                                </div>
                            ))}

                            {rewardPointsEnabled && (
                                <div className="border rounded-lg p-3 my-2 bg-gray-50 space-y-2">
                                    {redeemablePoints > 0 ? (
                                        <>
                                            <div className="text-sm text-gray-700">
                                                {language === "ar"
                                                    ? `عندك ${redeemablePoints} ${rewardPointsName} = ${redeemableAmountFull} جنيه. اختار كام تستخدم:`
                                                    : `You have ${redeemablePoints} ${rewardPointsName} = EGP ${redeemableAmountFull}. Choose how many to use:`}
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <input
                                                    type="number"
                                                    min={0}
                                                    max={redeemablePoints}
                                                    value={pointsToRedeem}
                                                    onChange={(e) => {
                                                        const val = parseFloat(e.target.value) || 0;
                                                        setPointsToRedeem(Math.max(0, Math.min(val, redeemablePoints)));
                                                    }}
                                                    className="border rounded px-2 py-1 w-24 text-center"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => setPointsToRedeem(redeemablePoints)}
                                                    className="text-xs text-mainColor underline"
                                                >
                                                    {language === "ar" ? "استخدام الكل" : "Use all"}
                                                </button>
                                                {pointsToRedeem > 0 && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setPointsToRedeem(0)}
                                                        className="text-xs text-gray-500 underline"
                                                    >
                                                        {language === "ar" ? "إلغاء" : "Clear"}
                                                    </button>
                                                )}
                                            </div>
                                        </>
                                    ) : (
                                        <div className="text-xs text-gray-500">
                                            {language === "ar"
                                                ? `ليس لديك ${rewardPointsName} كافية للاستبدال حاليًا`
                                                : `You don't have enough ${rewardPointsName} to redeem yet`}
                                        </div>
                                    )}

                                    {pointsToEarn > 0 && (
                                        <div className="text-xs text-green-700">
                                            {language === "ar"
                                                ? `هتكسب ${pointsToEarn} ${rewardPointsName} من هذا الطلب`
                                                : `You will earn ${pointsToEarn} ${rewardPointsName} from this order`}
                                        </div>
                                    )}
                                </div>
                            )}

                            {rewardDiscount > 0 && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">
                                        {language === "ar" ? "خصم نقاط المكافآت:" : "Reward points discount:"}
                                    </span>
                                    <span className="font-medium text-green-600">
                                        - EGP {rewardDiscount.toFixed(0)}
                                    </span>
                                </div>
                            )}

                            <div className="flex justify-between">
                                <span className="text-gray-600">{language === "ar" ? "الشحن:" : "Shipping:"}</span>
                                <span className="font-medium text-mainColor">
                                    {/* EGP {jsonUserData?.governorate?.price} */}
                                    EGP {deliveryPrice}
                                </span>
                            </div>

                            {jsonUserData?.governorate?.time_val && (
                                <div className="flex justify-between">
                                    <span className="text-gray-600">
                                        {language === "ar" ? "وقت التوصيل:" : "Delivery time:"}
                                    </span>
                                    <span className="font-medium text-mainColor">
                                        {jsonUserData.governorate.time_val} {t.hours}
                                    </span>
                                </div>
                            )}

                            {deliveryPrice && (
                                <div className="flex justify-between items-center">
                                    <span className="text-gray-600">
                                        {language === "ar" ? "المكان:" : "The place:"}
                                    </span>
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-mainColor">{userGovernorate}</span>
                                        <button
                                            type="button"
                                            onClick={() => setIsGovModalOpen(true)}
                                            className="text-xs underline text-gray-400 hover:text-mainColor transition"
                                        >
                                            {language === "ar" ? "تغيير" : "Change"}
                                        </button>
                                    </div>
                                </div>
                            )}

                            <hr className="my-3" />

                            <div className="flex justify-between text-lg font-bold">
                                <span>{language === "ar" ? "الإجمالي:" : "Total:"}</span>
                                <span className="text-mainColor">
                                    EGP{" "}
                                    {/* {(
                                        parseFloat(cartTotal) + parseFloat(jsonUserData?.governorate?.price || "0")
                                    ).toFixed(0)} */}
                                    {(
                                        parseFloat(cartTotal) -
                                        offersDiscount -
                                        rewardDiscount +
                                        parseFloat(deliveryPrice)
                                    ).toFixed(0)}
                                </span>
                            </div>
                        </div>

                        {/* Payment Methods - كروت شبكية، مفيش اختيار ديفولت، لازم اليوزر يختار */}
                        <div className="mt-6">
                            <div className="flex items-center justify-between mb-3">
                                <h4 className="text-md font-semibold">
                                    {language === "ar" ? "طريقة الدفع" : "Payment Method"}
                                </h4>
                                {!selectedPaymentMethod && (
                                    <span className="text-xs font-semibold text-red-500">
                                        {language === "ar" ? "* اختيار مطلوب" : "* Required"}
                                    </span>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                {PAYMENT_METHODS.map((method) => {
                                    const isSelected = selectedPaymentMethod === method.key;
                                    return (
                                        <button
                                            key={method.key}
                                            type="button"
                                            disabled={!method.available}
                                            onClick={() => handlePaymentMethodChange(method.key)}
                                            className={`relative flex flex-col items-center justify-center gap-2 min-h-[110px] p-3 rounded-2xl border-2 text-center transition-all duration-200 active:scale-95
                                                ${
                                                    isSelected
                                                        ? "border-mainColor bg-red-50 shadow-sm"
                                                        : "border-gray-200 bg-white hover:border-gray-300"
                                                }
                                                ${!method.available ? "opacity-40 cursor-not-allowed" : "cursor-pointer"}`}
                                        >
                                            {isSelected && (
                                                <span className="absolute top-2 left-2 w-5 h-5 rounded-full bg-mainColor text-white flex items-center justify-center text-[11px] leading-none">
                                                    ✓
                                                </span>
                                            )}
                                            <div className="w-10 h-10 flex items-center justify-center">
                                                {method.icon}
                                            </div>
                                            <div>
                                                <div className="font-semibold text-sm text-gray-800">
                                                    {method.label}
                                                </div>
                                                <div className="text-[11px] text-gray-500 mt-0.5 leading-tight">
                                                    {method.description}
                                                </div>
                                            </div>
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        {!jsonUserData || !jsonUserData.contact_id ? (
                            <div className="mt-6 space-y-2">
                                <div className="text-sm text-gray-600 text-center mb-2">
                                    {language === "ar"
                                        ? "سجّل دخولك أو اعمل حساب جديد عشان تكمل طلبك"
                                        : "Log in or create an account to complete your order"}
                                </div>
                                {/* حساب جديد — أحمر وبارز */}
                                <button
                                    className="w-full py-3 rounded-lg font-bold text-base text-white transition active:scale-95"
                                    style={{
                                        backgroundColor: "#c0392b",
                                        border: "2px solid #c0392b",
                                        fontFamily: "Cairo, sans-serif",
                                    }}
                                    onClick={() => navigate("/signUp", {state: {from: location.pathname}})}
                                >
                                    {language === "ar" ? "حساب جديد" : "Create account"}
                                </button>
                                {/* تسجيل الدخول — أبيض بإطار */}
                                <button
                                    className="w-full py-3 rounded-lg font-bold text-base transition active:scale-95"
                                    style={{
                                        backgroundColor: "#ffffff",
                                        border: "2px solid #c0392b",
                                        color: "#c0392b",
                                        fontFamily: "Cairo, sans-serif",
                                    }}
                                    onClick={() => navigate("/Login", {state: {from: location.pathname}})}
                                >
                                    {language === "ar" ? "تسجيل الدخول" : "Log in"}
                                </button>
                            </div>
                        ) : (
                            <Button
                                type="primary"
                                size="large"
                                className="w-full mt-6"
                                onClick={handleConfirmOrder}
                                loading={isCreatingOrder}
                                disabled={cartItems.length === 0 || !selectedPaymentMethod}
                            >
                                {isCreatingOrder
                                    ? language === "ar"
                                        ? "جاري الإرسال..."
                                        : "Processing..."
                                    : language === "ar"
                                    ? "تأكيد الطلب"
                                    : "Confirm Order"}
                            </Button>
                        )}

                        <Button
                            type="default"
                            size="large"
                            className="w-full mt-3"
                            onClick={handleContinueShopping}
                            disabled={isCreatingOrder}
                        >
                            {language === "ar" ? "متابعة التسوق" : "Continue Shopping"}
                        </Button>
                    </div>
                </div>
            </div>

            {/* Clear Cart Confirmation Modal */}
            <Modal
                title={language === "ar" ? "تأكيد تفريغ السلة" : "Confirm Clear Cart"}
                open={isClearModalOpen}
                onCancel={handleCloseClearModal}
                footer={[
                    <Button key="cancel" onClick={handleCloseClearModal}>
                        {language === "ar" ? "إلغاء" : "Cancel"}
                    </Button>,
                    <Button key="clear" type="primary" danger onClick={handleClearCart} loading={isCreatingOrder}>
                        {language === "ar" ? "تفريغ السلة" : "Clear Cart"}
                    </Button>,
                ]}
            >
                <p>
                    {language === "ar"
                        ? "هل أنت متأكد من أنك تريد تفريغ السلة؟ سيتم حذف جميع المنتجات."
                        : "Are you sure you want to clear the cart? All products will be removed."}
                </p>
            </Modal>

            {/* Order Confirmation Modal */}
            <Modal
                title={language === "ar" ? "تأكيد الطلب" : "Confirm Order"}
                open={isConfirmModalOpen}
                onCancel={handleCloseConfirmModal}
                footer={[
                    <Button key="cancel" onClick={handleCloseConfirmModal}>
                        {language === "ar" ? "إلغاء" : "Cancel"}
                    </Button>,
                    <Button key="confirm" type="primary" onClick={handleConfirmCODOrder} loading={isCreatingOrder}>
                        {isCreatingOrder
                            ? language === "ar"
                                ? "جاري الإرسال..."
                                : "Processing..."
                            : language === "ar"
                            ? "تأكيد الطلب"
                            : "Confirm Order"}
                    </Button>,
                ]}
            >
                <div className="space-y-4">
                    {/* نص مواعيد العمل الثابت اللي كتبه الأدمن */}
                    {workHoursData?.data?.note && (
                        <div style={{
                            background: "#eef6ff",
                            border: "1px solid #90caf9",
                            borderRadius: 8,
                            padding: "10px 16px",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            direction: "rtl",
                        }}>
                            <span style={{ fontSize: 18, flexShrink: 0 }}>🕐</span>
                            <p style={{ color: "#0d47a1", margin: 0, fontSize: 14, fontFamily: "Cairo, sans-serif" }}>
                                {workHoursData.data.note}
                            </p>
                        </div>
                    )}

                    {/* تنبيه مواعيد العمل */}
                    {workHoursData && !workHoursData.is_open_now && (
                        <div style={{
                            background: "#fff3cd",
                            border: "1px solid #ffc107",
                            borderRadius: 8,
                            padding: "12px 16px",
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                            direction: "rtl",
                        }}>
                            <span style={{ fontSize: 22, flexShrink: 0 }}>⚠️</span>
                            <div>
                                <p style={{ fontWeight: "bold", color: "#856404", margin: 0, fontFamily: "Cairo, sans-serif" }}>
                                    المتجر مغلق حالياً
                                </p>
                                {workHoursData.message && (
                                    <p style={{ color: "#533f03", margin: "4px 0 0", fontSize: 14, fontFamily: "Cairo, sans-serif" }}>
                                        {workHoursData.message}
                                    </p>
                                )}
                                <p style={{ color: "#856404", margin: "4px 0 0", fontSize: 13, fontFamily: "Cairo, sans-serif" }}>
                                    سيتم تنفيذ طلبك عند بداية ساعات العمل.
                                </p>
                            </div>
                        </div>
                    )}

                    {workHoursData?.is_open_now && workHoursData?.today && (
                        <div style={{
                            background: "#d4edda",
                            border: "1px solid #28a745",
                            borderRadius: 8,
                            padding: "10px 16px",
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            direction: "rtl",
                        }}>
                            <span style={{ fontSize: 18 }}>✅</span>
                            <p style={{ margin: 0, color: "#155724", fontSize: 14, fontFamily: "Cairo, sans-serif" }}>
                                المتجر مفتوح الآن — مواعيد اليوم:{" "}
                                <strong>{formatTime12h(workHoursData.today.open_time)}</strong> إلى{" "}
                                <strong>{formatTime12h(workHoursData.today.close_time)}</strong>
                            </p>
                        </div>
                    )}

                    <p>
                        {language === "ar"
                            ? "هل أنت متأكد من تأكيد هذا الطلب؟"
                            : "Are you sure you want to confirm this order?"}
                    </p>
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">
                                {language === "ar" ? "طريقة الدفع:" : "Payment Method:"}
                            </span>
                            <span className="font-medium">{getSelectedPaymentMethodLabel()}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">{language === "ar" ? "عدد المنتجات:" : "Items:"}</span>
                            <span className="font-medium">{cartItemsCount}</span>
                        </div>
                        <div className="flex justify-between mb-2">
                            <span className="text-gray-600">{language === "ar" ? "العنوان:" : "Address:"}</span>
                            <span className="font-medium text-sm">{getFullAddress()}</span>
                        </div>
                        {appliedOffers.map((offer, idx) => (
                            <div className="flex justify-between mb-2" key={idx}>
                                <span className="text-gray-600">{language === "ar" ? offer.label + ":" : `${offer.productName} offer discount:`}</span>
                                <span className="font-medium text-green-600">- EGP {offer.discount.toFixed(0)}</span>
                            </div>
                        ))}
                        {rewardDiscount > 0 && (
                            <div className="flex justify-between mb-2">
                                <span className="text-gray-600">{language === "ar" ? "خصم نقاط المكافآت:" : "Reward points discount:"}</span>
                                <span className="font-medium text-green-600">- EGP {rewardDiscount.toFixed(0)}</span>
                            </div>
                        )}
                        <div className="flex justify-between">
                            <span className="text-gray-600">{language === "ar" ? "الإجمالي:" : "Total:"}</span>
                            <span className="font-bold text-mainColor">
                                EGP{" "}
                                {/* {(parseFloat(cartTotal) + parseFloat(jsonUserData?.governorate?.price || "0")).toFixed(
                                    0
                                )} */}
                                {(
                                    parseFloat(cartTotal) -
                                    offersDiscount -
                                    rewardDiscount +
                                    parseFloat(deliveryPrice)
                                ).toFixed(0)}
                            </span>
                        </div>
                    </div>
                </div>
            </Modal>

            {/* Out of Stock Warning Modal */}
            <Modal
                title={language === "ar" ? "منتجات غير متاحة" : "Out of Stock Items"}
                open={isOutOfStockModalOpen}
                onCancel={handleCloseOutOfStockModal}
                footer={[
                    <Button key="cancel" onClick={handleCloseOutOfStockModal}>
                        {language === "ar" ? " تعديل الطلب  " : "Cancel Order"}
                    </Button>,
                    <Button
                        key="proceed"
                        type="primary"
                        onClick={handleProceedWithAvailableItems}
                        loading={isCreatingOrder}
                    >
                        {language === "ar" ? "تأكيد مع المنتجات المتاحة" : "Proceed with Available Items"}
                    </Button>,
                ]}
            >
                <div className="space-y-4">
                    <div className="flex items-center gap-2 mb-4">
                        <div className="text-yellow-500 text-xl">⚠️</div>
                        <p className="text-lg font-medium">
                            {language === "ar"
                                ? "المنتجات التالية غير متاحة حالياً:"
                                : "The following items are currently out of stock:"}
                        </p>
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <ul className="space-y-2">
                            {outOfStockItems.map((item, index) => (
                                <li
                                    key={index}
                                    className="flex justify-between items-center py-2 border-b border-yellow-200 last:border-b-0"
                                >
                                    <span className="font-medium text-gray-800">{item?.item_name || item?.name}</span>
                                    <span className="text-sm text-gray-600">
                                        {language === "ar" ? "غير متاح" : "Out of Stock"}
                                    </span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <p className="text-sm text-blue-800">
                            {language === "ar"
                                ? "هل تريد المتابعة مع المنتجات المتاحة فقط؟ سيتم حذف المنتجات غير المتاحة من السلة."
                                : "Do you want to proceed with available items only? Out-of-stock items will be removed from your cart."}
                        </p>
                    </div>
                </div>
            </Modal>

            {/* ── Governorate picker popup ── */}
            {isGovModalOpen && (
                <div
                    className="fixed inset-0 z-50 flex items-end md:items-center justify-center"
                    style={{background: "rgba(0,0,0,0.5)"}}
                    onClick={() => { setIsGovModalOpen(false); setGovSearchTerm(""); }}
                >
                    <div
                        className="bg-white w-full md:max-w-sm rounded-t-2xl md:rounded-2xl shadow-2xl flex flex-col"
                        style={{maxHeight: "80vh"}}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-5 pt-5 pb-3 border-b flex-shrink-0">
                            <h3 className="font-bold text-lg" style={{fontFamily: "Cairo, sans-serif"}}>
                                {language === "ar" ? "اختار منطقة التوصيل" : "Select delivery area"}
                            </h3>
                            <button
                                onClick={() => { setIsGovModalOpen(false); setGovSearchTerm(""); }}
                                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                            >×</button>
                        </div>

                        {/* Search */}
                        <div className="px-4 py-3 flex-shrink-0">
                            <div className="relative">
                                <span className="absolute top-1/2 -translate-y-1/2 right-3 text-gray-400">🔍</span>
                                <input
                                    type="text"
                                    value={govSearchTerm}
                                    onChange={(e) => setGovSearchTerm(e.target.value)}
                                    placeholder={language === "ar" ? "ابحث عن المنطقة..." : "Search area..."}
                                    autoFocus
                                    className="w-full pr-9 pl-4 py-2 border border-gray-200 rounded-xl text-sm outline-none focus:border-mainColor"
                                    style={{fontFamily: "Cairo, sans-serif", direction: "rtl"}}
                                />
                            </div>
                        </div>

                        {/* List — scrollable, independent from page scroll */}
                        <div
                            className="overflow-y-auto flex-1 px-2 pb-4"
                            style={{overscrollBehavior: "contain"}}
                        >
                            {(governorateData?.data ?? [])
                                .filter((gov) =>
                                    !govSearchTerm.trim() ||
                                    gov.name?.toLowerCase().includes(govSearchTerm.trim().toLowerCase())
                                )
                                .map((gov) => (
                                    <button
                                        key={gov.id}
                                        type="button"
                                        onClick={() => handlePickGovernorate(gov)}
                                        className="w-full flex justify-between items-center px-4 py-3 rounded-xl hover:bg-red-50 active:bg-red-100 transition text-right"
                                        style={{fontFamily: "Cairo, sans-serif"}}
                                    >
                                        <span className="font-medium text-gray-800">{gov.name}</span>
                                    </button>
                                ))}
                            {(governorateData?.data ?? []).filter((gov) =>
                                !govSearchTerm.trim() ||
                                gov.name?.toLowerCase().includes(govSearchTerm.trim().toLowerCase())
                            ).length === 0 && (
                                <p className="text-center text-gray-400 py-8" style={{fontFamily: "Cairo, sans-serif"}}>
                                    {language === "ar" ? "مفيش نتائج" : "No results"}
                                </p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CartList;
